'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { ChevronLeft, ChevronRight, Download, Eye, Star, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useRealtimeContent } from '@/hooks/useRealtimeContent';

interface FeaturedItem {
  id: string;
  title: string;
  slug: string;
  description: string;
  year: number;
  imdb_rating: number;
  genre: string;
  language: string;
  poster_url: string;
  backdrop_url: string;
  type: 'movie' | 'series';
}

const INTERVAL = 6000;

export default function HeroCarousel() {
  const [featured, setFeatured] = useState<FeaturedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const supabase = createClient();

  const fetchFeatured = useCallback(async () => {
    const [moviesRes, seriesRes] = await Promise.all([
      supabase
        .from('movies')
        .select('id, title, slug, description, year, imdb_rating, genre, language, poster_url, backdrop_url')
        .eq('featured', true)
        .order('created_at', { ascending: false })
        .limit(6),
      supabase
        .from('series')
        .select('id, title, slug, description, year, imdb_rating, genre, language, poster_url, backdrop_url')
        .eq('featured', true)
        .order('created_at', { ascending: false })
        .limit(4),
    ]);

    const movies: FeaturedItem[] = (moviesRes.data ?? []).map((m) => ({ ...m, type: 'movie' as const }));
    const series: FeaturedItem[] = (seriesRes.data ?? []).map((s) => ({ ...s, type: 'series' as const }));
    const combined = [...movies, ...series];
    setFeatured(combined);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchFeatured();
  }, [fetchFeatured]);

  useRealtimeContent({
    tables: ['movies', 'series'],
    onAnyChange: () => fetchFeatured(),
  });

  const goTo = useCallback((index: number) => {
    if (isTransitioning || featured.length === 0) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, featured.length]);

  const next = useCallback(() => {
    if (featured.length === 0) return;
    goTo((current + 1) % featured.length);
  }, [current, goTo, featured.length]);

  const prev = useCallback(() => {
    if (featured.length === 0) return;
    goTo((current - 1 + featured.length) % featured.length);
  }, [current, goTo, featured.length]);

  useEffect(() => {
    if (featured.length === 0) return;
    const timer = setInterval(next, INTERVAL);
    return () => clearInterval(timer);
  }, [next, featured.length]);

  if (loading) {
    return (
      <div className="relative w-full h-[70vh] min-h-[500px] max-h-[750px] bg-background/50 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  if (featured.length === 0) {
    return (
      <div className="relative w-full h-[40vh] min-h-[300px] bg-background/50 flex items-center justify-center">
        <p className="text-muted-foreground font-body text-sm">No featured posts yet. Mark movies or series as featured in the admin dashboard.</p>
      </div>
    );
  }

  const item = featured[current];
  const detailHref = item.type === 'series' ? `/series/${item.slug}` : `/movie-detail-page`;

  return (
    <div className="relative w-full h-[70vh] min-h-[500px] max-h-[750px] overflow-hidden mt-0">
      {/* Backdrop images */}
      {featured.map((m, i) => (
        <div
          key={`carousel-backdrop-${m.id}`}
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: i === current ? 1 : 0 }}
        >
          <AppImage
            src={m.backdrop_url || m.poster_url}
            alt={`${m.title} backdrop — ${m.genre} ${m.type}`}
            fill
            priority={i === 0}
            sizes="100vw"
            className="object-cover"
          />
        </div>
      ))}

      {/* Gradients */}
      <div className="absolute inset-0 carousel-gradient-bottom" />
      <div className="absolute inset-0 carousel-gradient-left" />
      <div className="absolute inset-0 bg-background/30" />

      {/* Content */}
      <div
        className="absolute inset-0 flex items-end pb-16 lg:pb-20"
        key={`carousel-content-${current}`}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 w-full">
          <div className="max-w-2xl animate-slide-up">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="genre-badge">{item.genre}</span>
              <span className="text-xs font-mono-data text-muted-foreground">{item.year}</span>
              <div className="flex items-center gap-1">
                <Star size={12} className="rating-star fill-yellow-400" />
                <span className="text-xs font-bold font-mono-data text-foreground">{item.imdb_rating}</span>
              </div>
              <span className="text-xs text-muted-foreground font-body">{item.language}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent/80 text-white uppercase">{item.type}</span>
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground leading-tight mb-3">
              {item.title}
            </h1>

            {/* Description */}
            <p className="text-sm sm:text-base text-muted-foreground font-body leading-relaxed mb-6 line-clamp-3 max-w-xl">
              {item.description}
            </p>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3">
              <Link
                href={detailHref}
                className="flex items-center gap-2 px-6 py-3 neon-btn-solid-cyan rounded-xl text-sm font-bold"
              >
                <Download size={16} />
                Download Now
              </Link>
              <Link
                href={detailHref}
                className="flex items-center gap-2 px-6 py-3 neon-btn-cyan rounded-xl text-sm font-bold"
              >
                <Eye size={16} />
                View Details
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation arrows */}
      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 p-2.5 glass-card rounded-full border border-border hover:border-primary/50 hover:text-primary transition-all duration-200 text-muted-foreground"
        aria-label="Previous slide"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-2.5 glass-card rounded-full border border-border hover:border-primary/50 hover:text-primary transition-all duration-200 text-muted-foreground"
        aria-label="Next slide"
      >
        <ChevronRight size={20} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {featured.map((_, i) => (
          <button
            key={`carousel-dot-${i}`}
            onClick={() => goTo(i)}
            className={`rounded-full transition-all duration-300 ${
              i === current
                ? 'w-6 h-2 bg-primary shadow-[0_0_8px_var(--neon-cyan)]'
                : 'w-2 h-2 bg-white/30 hover:bg-white/60'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Slide counter */}
      <div className="absolute top-20 right-6 font-mono-data text-xs text-muted-foreground">
        <span className="text-primary font-bold">{String(current + 1).padStart(2, '0')}</span>
        <span> / {String(featured.length).padStart(2, '0')}</span>
      </div>
    </div>
  );
}